import { memo, useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeHighlight from "rehype-highlight";
import "highlight.js/styles/github-dark.css";
import type { ChatMessage, MessageAttachment, ProgressEntry } from "../hooks/useChatStream";
import type { FriendlyError } from "../api/errorMessages";
import { DAEMON_URL } from "../config";
import { fitWithin } from "../lib/attachments";
import ElapsedTimer from "./ElapsedTimer";

type Props = {
  message: ChatMessage;
  /** Handler for the one-click Continue button (resume a stalled turn). */
  onContinue?: () => void;
};

const NODE_GLYPH: Record<ProgressEntry["kind"], string> = {
  tool_use: "",
  tool_ok: "✓",
  tool_error: "✗",
  thinking: "",
  sibling_conflict: "!",
};

function countTools(entries: ProgressEntry[]): number {
  return entries.filter((e) => e.kind === "tool_use").length;
}

function countConflicts(entries: ProgressEntry[]): number {
  return entries.filter((e) => e.kind === "sibling_conflict").length;
}

/**
 * Deep mode summary: how many parallel sub-agents this turn ran. Counts distinct
 * spawned agents (agentSpawn frames) unioned with any agent that produced
 * attributed progress (agentId). Zero for a normal single-agent turn, which
 * therefore renders with no deep-mode chrome at all.
 */
function deepAgentCount(entries: ProgressEntry[]): number {
  const reportingAgents = new Set<string>();
  let spawns = 0;
  for (const e of entries) {
    if (e.agentSpawn) spawns += 1;
    if (e.agentId) reportingAgents.add(e.agentId);
  }
  // The daemon emits one spawn frame per launched agent; distinct agentIds count
  // agents that produced attributed progress. Either may lead (a spawned agent
  // might report nothing yet), so take the larger.
  return Math.max(spawns, reportingAgents.size);
}

function summarize(entries: ProgressEntry[]): string {
  const tools = countTools(entries);
  const parts: string[] = [];
  if (tools > 0) parts.push(`${tools} tool${tools === 1 ? "" : "s"}`);
  // The trail auto-collapses when the turn settles, so anything the user must
  // act on has to survive into the one-line summary. A sibling conflict is the
  // only entry that outlives the turn: the files are still divergent afterwards.
  const conflicts = countConflicts(entries);
  if (conflicts > 0) parts.push(`${conflicts} conflict${conflicts === 1 ? "" : "s"}`);
  return parts.length > 0 ? parts.join(", ") : "Done";
}

/**
 * Forge progress trail — a connected timeline. While streaming it shows a
 * spinner and stays open; once the response settles it collapses to a one-line
 * summary ("5 tools") and the user can re-open it.
 */
function ProgressTrail({
  entries,
  streaming,
  startedAt,
  overflow = 0,
}: {
  entries: ProgressEntry[];
  streaming: boolean;
  startedAt?: number;
  /** How many earlier progress entries were dropped to keep the list bounded. */
  overflow?: number;
}) {
  const [open, setOpen] = useState(streaming);

  // Auto-open while streaming, auto-collapse when it finishes.
  useEffect(() => {
    setOpen(streaming);
  }, [streaming]);

  if (entries.length === 0 && !streaming) return null;

  const toolCount = countTools(entries);
  const agentCount = deepAgentCount(entries);

  return (
    <div className={`progress-trail${open ? " open" : ""}`}>
      <button
        type="button"
        className="progress-trail-head"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        {streaming ? (
          <span className="progress-trail-spin" aria-hidden="true" />
        ) : (
          <span className="progress-trail-done" aria-hidden="true" />
        )}
        <span className="progress-trail-summary">
          {streaming ? "Working…" : summarize(entries)}
        </span>
        <span className="progress-trail-meta">
          {!streaming && toolCount > 0 && <span>{toolCount} tools</span>}
          {startedAt != null && <ElapsedTimer startedAt={startedAt} streaming={streaming} />}
          <span className="progress-trail-chev" aria-hidden="true">›</span>
        </span>
      </button>
      <div className="progress-trail-body">
        {agentCount > 0 && (
          <div className="progress-deepmode" role="status">
            <span className="progress-deepmode-dot" aria-hidden="true" />
            Deep mode: {agentCount} agent{agentCount === 1 ? "" : "s"} working
          </div>
        )}
        {overflow > 0 && (
          <div className="progress-overflow" aria-hidden="true">
            +{overflow} earlier step{overflow === 1 ? "" : "s"}
          </div>
        )}
        {entries.map((e) => (
          <div
            key={e.id}
            className={`progress-item progress-${e.kind}${e.agentId || e.agentSpawn ? " progress-agent" : ""}`}
          >
            <span className="progress-rail">
              <span className="progress-node">{NODE_GLYPH[e.kind]}</span>
            </span>
            <span className="progress-label">{e.label}</span>
            {(e.agentId || e.agentSpawn) && (
              <span className="progress-agent-badge">{e.agentType ?? "agent"}</span>
            )}
          </div>
        ))}
        {streaming && (
          <div className="progress-item progress-pulse">
            <span className="progress-rail">
              <span className="progress-node" />
            </span>
            <span className="progress-label">working…</span>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * The box a thumbnail is drawn in, and the same number as the max-width /
 * max-height on `.message-attachment img` in styles.css. The two have to agree:
 * the point of the width/height attributes below is that the CSS caps never
 * actually bind, so a presentational hint can never fight them.
 */
const THUMB_MAX_PX = 220;

type Thumbnail = {
  key: string;
  src: string;
  /**
   * Where "open full size in a new tab" goes, or null when the thumbnail must
   * not be a link at all. Only a served URL is linkable: Chrome and Firefox
   * both block top-level navigation to a `data:` URL, so linking a just-sent
   * image would render a clickable-looking thumbnail that does nothing — and
   * only until the next reload, when the same image comes back as a served URL
   * and starts working. A plain image is honest; a dead link is not.
   */
  href: string | null;
  name: string;
  /** The rendered size, when the source knew its dimensions. */
  box: { width: number; height: number } | null;
};

/**
 * The size this image will occupy once it loads, or null when the metadata does
 * not say. Given to the `<img>` as width/height so the browser reserves the
 * space up front: these load lazily, and the transcript auto-scrolls, so a row
 * of images resolving one by one would walk the page out from under the reader.
 *
 * The SCALED size, not the intrinsic one. An HTML width/height pair is a
 * presentational hint for the CSS `width`/`height` properties, so handing over a
 * 1568px-wide original would set `height` to the full original height while
 * `max-width` squeezed the width to 220 — a badly stretched thumbnail. Scaling
 * here keeps both axes inside the cap, so the caps never bind.
 */
function thumbBox(w: unknown, h: unknown): { width: number; height: number } | null {
  if (typeof w !== "number" || typeof h !== "number") return null;
  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return null;
  return fitWithin(w, h, THUMB_MAX_PX);
}

/**
 * What to render for one attachment, or null when the entry cannot be rendered.
 *
 * Defensive on purpose. A stored image's metadata is declared twice — once in
 * `api/types.ts` and once in the daemon — with nothing asserting the two agree,
 * so a rename on the daemon side arrives here as `undefined` at runtime rather
 * than as a type error. A single bad entry drops out of the row; it does not get
 * to blank the message it was attached to.
 */
function thumbnail(a: MessageAttachment, index: number): Thumbnail | null {
  if (!a || typeof a !== "object") return null;
  if (a.kind === "local") {
    const at = a.attachment;
    // Keep the scheme check even though this no longer becomes an href: it is
    // what guarantees only the local encoder's output is ever rendered here,
    // and it is the guard that would have to hold again the day this string
    // does become navigable.
    if (!at || typeof at.dataUrl !== "string" || !at.dataUrl.startsWith("data:image/")) return null;
    return {
      key: at.id || `local-${index}`,
      src: at.dataUrl,
      href: null,
      name: at.name || "image",
      box: thumbBox(at.width, at.height),
    };
  }
  if (a.kind === "stored") {
    const meta = a.meta;
    if (!meta || typeof meta.id !== "string" || meta.id.length === 0) return null;
    if (!Number.isInteger(a.conversationId)) return null;
    // No Authorization header can ride on an <img>, so the bytes are fetched by
    // the browser on the session cookie, which the daemon's route accepts. That
    // is also why this is a plain `src` and not a fetch through the API client,
    // and why there is no `crossorigin` attribute: setting one turns this into a
    // CORS request that would drop the cookie.
    const src = `${DAEMON_URL}/attachments/${a.conversationId}/${encodeURIComponent(meta.id)}`;
    return {
      key: meta.id,
      src,
      // A real http(s) URL, so opening it in a tab works — and the same cookie
      // that fetched the thumbnail authenticates the full-size request.
      href: src,
      name: typeof meta.name === "string" && meta.name.length > 0 ? meta.name : "image",
      box: thumbBox(meta.width, meta.height),
    };
  }
  return null;
}

function thumbnails(attachments: MessageAttachment[] | undefined): Thumbnail[] {
  if (!Array.isArray(attachments)) return [];
  return attachments.flatMap((a, i) => {
    const t = thumbnail(a, i);
    return t ? [t] : [];
  });
}

function ErrorNotice({ info }: { info: FriendlyError }) {
  return (
    <div className={`message-notice sev-${info.severity}`} role="alert">
      <div className="message-notice-title">{info.title}</div>
      <div className="message-notice-body">{info.body}</div>
      {info.hint ? <div className="message-notice-hint">{info.hint}</div> : null}
    </div>
  );
}

function Message({ message, onContinue }: Props) {
  const { role, content, streaming, error, errorInfo, progress, progressOverflow, interrupted, startedAt, autoContinue, resuming, resumingLabel, needsContinue } = message;

  // Auto-injected / one-click "continue" user turns render as a compact,
  // de-emphasized chip rather than a full bubble — they are plumbing to resume a
  // stalled response, not something the user typed.
  if (role === "user" && autoContinue) {
    return (
      <div className="message user auto-continue">
        <div className="message-content">
          <span className="auto-continue-chip" title="Sent automatically to resume the response">
            <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M21 2v6h-6M3 12a9 9 0 0 1 15-6.7L21 8M3 22v-6h6M21 12a9 9 0 0 1-15 6.7L3 16" />
            </svg>
            continue
          </span>
        </div>
      </div>
    );
  }

  const thumbs = thumbnails(message.attachments);
  // An image with no caption is a real prompt, so a turn showing a picture and
  // nothing else is not the empty turn "(empty)" is there to explain.
  const showEmpty = !content && !streaming && !interrupted && !errorInfo && !resuming && thumbs.length === 0;
  return (
    <div className={`message ${role}${error ? " error" : ""}${interrupted ? " interrupted" : ""}`}>
      <div className="message-content">
        <div className="message-role">{role === "assistant" ? "ThunderAy AI" : role}</div>
        {thumbs.length > 0 && (
          <div className="message-attachments">
            {thumbs.map((t) => {
              // Lazy because a reloaded transcript can hold five images per turn
              // across dozens of turns, and every one of them is its own request
              // to the daemon the moment it is in the DOM.
              const img = (
                <img src={t.src} alt={t.name} loading="lazy" width={t.box?.width} height={t.box?.height} />
              );
              // Linked only when the target is actually openable — see Thumbnail.href.
              return t.href ? (
                <a
                  className="message-attachment"
                  key={t.key}
                  href={t.href}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {img}
                </a>
              ) : (
                <span className="message-attachment" key={t.key}>{img}</span>
              );
            })}
          </div>
        )}
        {role === "assistant" ? (
          <div className="markdown-body">
            {(progress && progress.length > 0) || streaming ? (
              <ProgressTrail entries={progress ?? []} streaming={streaming} startedAt={startedAt} overflow={progressOverflow ?? 0} />
            ) : null}
            {content && (
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeHighlight]}
                components={{
                  a: ({ node, ...props }) => (
                    <a {...props} target="_blank" rel="noopener noreferrer" />
                  ),
                }}
              >
                {content}
              </ReactMarkdown>
            )}
            {errorInfo && !resuming ? <ErrorNotice info={errorInfo} /> : null}
            {showEmpty && <span className="message-empty">(empty)</span>}
            {streaming && content && <span className="streaming-caret" aria-hidden="true" />}
            {interrupted && <div className="message-interrupted">(stopped)</div>}
            {resuming && (
              <div className="message-resuming" role="status">
                <span className="message-resuming-dot" aria-hidden="true" />
                {/* A deep-mode budget stop is continued deliberately, not
                    retried after a fault, so it supplies its own wording; every
                    other resume keeps the default. */}
                {resumingLabel ?? "Resuming automatically…"}
              </div>
            )}
            {needsContinue && onContinue && (
              <button type="button" className="continue-btn" onClick={onContinue}>
                <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M21 2v6h-6M3 12a9 9 0 0 1 15-6.7L21 8" />
                </svg>
                Continue
              </button>
            )}
            {/* The other half of the same offer, for the turns the stream never
                got to classify. `needsContinue` is the stream's own verdict
                that a turn can be resumed; `interrupted` is a turn cut short
                without one — a Stop the user now regrets, or one a daemon
                restart orphaned and a reload brought back as "(stopped)".

                `!needsContinue` and `!streaming` both guard the SAME thing, and
                it is worth being exact about what holds it up, because it is
                not this file and it is not the reducer. `interrupted` is
                STICKY: `statusFields` only ever adds it (useChatStream.ts:243)
                and both finalizers are `{...m, ...patch, streaming: false}`
                spreads that keep what a message already carries. So a message
                that took `interrupted` off a snapshot can take `needsContinue`
                off the `done` that follows, and both buttons would render.

                What actually prevents it is a DAEMON property: a turn's state
                becomes "interrupted" only inside `ActiveTurn.finalize`, which
                deregisters it through `onFinalize` → `manager.clearActiveTurn`
                (src/server.ts:1167) with no `await` in between, so the reattach
                GET's `manager.getActiveTurn` (src/server.ts:1282) can never
                hand back a turn whose snapshot would say "interrupted". Retain
                finalized turns in the registry for even a moment — the natural
                way to make `subscribe`'s late-replay branch (active-turn.ts:545)
                reachable — and the pair comes back. These two guards cost
                nothing and put the belt on the axis nothing local enforces. */}
            {interrupted && !needsContinue && !streaming && onContinue && (
              <button type="button" className="continue-btn retry-btn" onClick={onContinue}>
                <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M21 2v6h-6M3 12a9 9 0 0 1 15-6.7L21 8M3 22v-6h6M21 12a9 9 0 0 1-15 6.7L3 16" />
                </svg>
                Retry
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Only when there is text. `.user-text` is a bordered, padded box,
                so rendering it around an empty string leaves a grey bar under
                the thumbnail of an image sent with no caption. */}
            {content && <pre className="user-text">{content}</pre>}
            {showEmpty && <span className="message-empty">(empty)</span>}
            {streaming && content && <span className="streaming-caret" aria-hidden="true" />}
          </>
        )}
        {error && !errorInfo && <div className="message-error">{error}</div>}
      </div>
    </div>
  );
}

// Memoized: message objects are replaced by reference only when their content
// changes, so a streaming token update or a growing transcript re-renders only
// the message that actually changed — not every prior ReactMarkdown body.
export default memo(Message);
